class SocialVideoCarousel extends HTMLElement {
  constructor() {
    super();

    this.track = null;
    this.prevButton = null;
    this.nextButton = null;
    this.raf = null;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-social-carousel-track]');
    this.prevButton = this.querySelector('[data-social-carousel-prev]');
    this.nextButton = this.querySelector('[data-social-carousel-next]');

    this.addEventListener('click', this.handleClick);
    this.track?.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.updateNavigation);

    this.initializeVideos();
    this.updateNavigation();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.track?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.updateNavigation);

    if (this.raf) window.cancelAnimationFrame(this.raf);
  }

  initializeVideos() {
    const cards = this.querySelectorAll('[data-social-card]');

    for (const card of cards) {
      const video = this.getCardVideo(card);

      if (!(video instanceof HTMLVideoElement)) continue;

      video.playsInline = true;
      card.dataset.muted = String(video.muted);
      card.dataset.playing = String(!video.paused);
      card.dataset.autoplay = String(video.dataset.autoplay === 'true' || video.autoplay);

      video.addEventListener('play', () => this.updateCardState(card, video));
      video.addEventListener('pause', () => this.updateCardState(card, video));
      video.addEventListener('volumechange', () => this.updateCardState(card, video));

      if (video.dataset.autoplay !== 'true') continue;

      this.playVideo(card, video);
    }
  }

  handleClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const playButton = target.closest('[data-social-play]');
    const muteButton = target.closest('[data-social-mute]');
    const prevButton = target.closest('[data-social-carousel-prev]');
    const nextButton = target.closest('[data-social-carousel-next]');

    if (playButton) {
      this.togglePlayback(playButton);
      return;
    }

    if (muteButton) {
      this.toggleMute(muteButton);
      return;
    }

    if (prevButton) {
      this.slide(-1);
      return;
    }

    if (nextButton) {
      this.slide(1);
    }
  };

  async togglePlayback(button) {
    const card = button.closest('[data-social-card]');
    const video = card ? this.getCardVideo(card) : null;

    if (!(card instanceof HTMLElement) || !(video instanceof HTMLVideoElement)) return;

    if (video.paused) {
      await this.playVideo(card, video);
      return;
    }

    video.pause();
  }

  async playVideo(card, video, options = {}) {
    const mutedFallbackAttempted = options.mutedFallbackAttempted === true;

    try {
      if (video.readyState === 0) video.load();

      await video.play();
      this.updateCardState(card, video);
    } catch {
      if (!mutedFallbackAttempted && !video.muted) {
        video.muted = true;
        await this.playVideo(card, video, { mutedFallbackAttempted: true });
        return;
      }

      card.dataset.playing = 'false';
    }
  }

  toggleMute(button) {
    const card = button.closest('[data-social-card]');
    const video = card ? this.getCardVideo(card) : null;

    if (!(card instanceof HTMLElement) || !(video instanceof HTMLVideoElement)) return;

    video.muted = !video.muted;
  }

  slide(direction) {
    if (!this.track) return;

    const card = this.track.querySelector('[data-social-card]');
    const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap || '0');
    const distance = card instanceof HTMLElement ? card.offsetWidth + gap : this.track.clientWidth;

    this.track.scrollBy({
      left: distance * direction,
      behavior: 'smooth',
    });
  }

  handleScroll = () => {
    if (this.raf) window.cancelAnimationFrame(this.raf);

    this.raf = window.requestAnimationFrame(this.updateNavigation);
  };

  updateNavigation = () => {
    if (!this.track || !this.prevButton || !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const currentScroll = this.track.scrollLeft;

    this.prevButton.disabled = currentScroll <= 1;
    this.nextButton.disabled = currentScroll >= maxScroll - 1;
  };

  updateCardState(card, video) {
    card.dataset.playing = String(!video.paused);
    card.dataset.muted = String(video.muted);

    const playButton = card.querySelector('[data-social-play]');
    const muteButton = card.querySelector('[data-social-mute]');

    playButton?.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
    muteButton?.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
  }

  getCardVideo(card) {
    const video = card.querySelector('[data-social-video], .social-carousel__video, video');

    return video instanceof HTMLVideoElement ? video : null;
  }
}

if (!customElements.get('social-video-carousel')) {
  customElements.define('social-video-carousel', SocialVideoCarousel);
}
